import axios from 'axios';
import { getAtsBaseUrl, mapAtsRole } from '../atsConfig';
import { useAuthStore } from '../../store/authStore';
import { AtsCardValidationError, formatAtsValidationErrors, parseAtsValidationErrors } from '../utils/validationErrors';
import type {
  AtsCandidateCard,
  AtsCandidateListItem,
  AtsCandidateListResponse,
  AtsFolderListResponse,
  AtsCurrencyRates,
  AtsSearchFilters,
  AtsDocument,
  AtsDuplicateCheck,
  AtsDuplicateGroup,
  AtsImportJob,
  AtsImportJobListResponse,
  AtsJson,
} from '../types';

type AtsBooleanSearchResponse = {
  items: Array<Omit<AtsCandidateListItem, 'documents'>>;
  total: number;
  page: number;
  page_size: number;
  pages: number;
};

const atsHttp = axios.create({
  baseURL: getAtsBaseUrl(),
});

atsHttp.interceptors.request.use((config) => {
  const { token, role, user } = useAuthStore.getState();
  config.headers = config.headers || {};
  const headers = config.headers as Record<string, string>;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  headers['x-ats-role'] = mapAtsRole(role || user?.role);
  headers['x-ats-user'] = String(user?.id || user?.email || '');
  headers['x-ats-user-name'] = encodeURIComponent(
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '',
  );
  return config;
});

const toTags = (tags?: string): string | undefined => tags?.split(',').map((tag) => tag.trim()).filter(Boolean).join(',');

const ATS_EXTRACT_TIMEOUT_MS = 180_000;
const ATS_PARSE_TIMEOUT_MESSAGE = 'Разбор ещё идёт на сервере. Закройте окно и обновите папку — карточка могла уже появиться.';

export class AtsParseTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, AtsParseTimeoutError.prototype);
  }
}

export type AtsResumeUpdateStatus = {
  status: 'not_found' | 'processing' | 'failed' | 'completed';
  document_id: string | null;
  document: AtsDocument | null;
  error_message: string | null;
};

export const sha256File = async (file: File): Promise<string> => (
  Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer())))
    .map((byte) => byte.toString(16).padStart(2, '0')).join('')
);

export type AtsDuplicateOverview = {
  groups: AtsDuplicateGroup[];
  scan_status: 'idle' | 'running' | 'completed' | 'failed';
  scan_error: string | null;
};

const isAtsGatewayTimeout = (error: unknown): boolean => {
  const axiosError = error as { code?: string; message?: string; response?: { status?: number } };
  return (
    axiosError.code === 'ECONNABORTED'
    || axiosError.response?.status === 504
    || axiosError.response?.status === 502
    || /timeout/i.test(axiosError.message || '')
  );
};

const getAtsErrorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as { code?: string; message?: string; response?: { status?: number; data?: unknown } };
  if (isAtsGatewayTimeout(error)) {
    return fallback;
  }
  const data = axiosError.response?.data;
  const validationErrors = parseAtsValidationErrors(data);
  if (validationErrors.length) return formatAtsValidationErrors(validationErrors);
  if (typeof data === 'object' && data !== null) {
    const record = data as Record<string, unknown>;
    const wrapped = record.error;
    if (typeof wrapped === 'object' && wrapped !== null) {
      const message = (wrapped as Record<string, unknown>).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    if (typeof record.detail === 'string' && record.detail.trim()) return record.detail;
    if (Array.isArray(record.detail)) {
      const messages = record.detail
        .map((item) => (
          typeof item === 'object' && item !== null
            ? String((item as Record<string, unknown>).msg || '')
            : ''
        ))
        .map((message) => message.replace(/^Value error,\s*/i, '').trim())
        .filter(Boolean);
      if (messages.length) return messages.join(' ');
    }
  }
  return fallback;
};

export const atsApi = {
  async getCurrencyRates(): Promise<AtsCurrencyRates> {
    const response = await atsHttp.get<AtsCurrencyRates>('/meta/currency-rates');
    return response.data;
  },

  async listCandidates(params: {
    page?: number;
    page_size?: number;
    query?: string;
    status?: string;
    folder_path?: string;
  } = {}): Promise<AtsCandidateListResponse> {
    const response = await atsHttp.get<AtsCandidateListResponse>('/candidates', { params });
    return response.data;
  },

  async listAllCandidates(params: {
    query?: string;
    status?: string;
    page_size?: number;
    folder_path?: string;
  } = {}): Promise<AtsCandidateListItem[]> {
    const pageSize = params.page_size || 100;
    const firstPage = await this.listCandidates({
      ...params,
      page: 1,
      page_size: pageSize,
    });
    const items = [...firstPage.items];

    for (let page = 2; page <= firstPage.pages; page += 1) {
      const response = await this.listCandidates({
        ...params,
        page,
        page_size: pageSize,
      });
      items.push(...response.items);
    }

    return items;
  },

  async booleanSearch(
    query: string,
    filters: AtsSearchFilters = {},
    { page = 1, pageSize = 100, searchScope = 'all' }: { page?: number; pageSize?: number; searchScope?: 'all' | 'full_name' } = {},
  ): Promise<AtsCandidateListResponse> {
    try {
      const response = await atsHttp.post<AtsBooleanSearchResponse>(
        '/search/boolean',
        {
          query,
          search_scope: searchScope,
          filters,
          page,
          page_size: pageSize,
        },
        { headers: { 'Content-Type': 'application/json' } },
      );
      return {
        ...response.data,
        items: response.data.items.map((item) => ({
          ...item,
          documents: [],
        })),
      };
    } catch (error) {
      throw new Error(getAtsErrorMessage(error, 'Не удалось выполнить поиск в Elasticsearch'));
    }
  },

  async getCandidateCard(candidateId: string): Promise<AtsCandidateCard> {
    const response = await atsHttp.get<AtsCandidateCard>(`/candidates/${candidateId}/card`);
    return response.data;
  },

  async getCandidatePhotoUrl(candidateId: string): Promise<string | null> {
    try {
      const response = await atsHttp.get<Blob>(`/candidates/${candidateId}/photo`, {
        responseType: 'blob',
      });
      if (!response.data || response.data.size === 0) return null;
      if (response.data.type.includes('json')) return null;
      return URL.createObjectURL(response.data);
    } catch {
      return null;
    }
  },

  async replaceCandidatePhoto(candidateId: string, file: File): Promise<AtsCandidateCard> {
    const data = new FormData();
    data.append('file', file);
    try {
      const response = await atsHttp.put<AtsCandidateCard>(
        `/candidates/${candidateId}/photo`, data,
      );
      return response.data;
    } catch (error) {
      throw new Error(getAtsErrorMessage(error, 'Не удалось загрузить фото кандидата'));
    }
  },

  async updateCandidateCard(candidateId: string, payload: Partial<AtsCandidateCard>): Promise<AtsCandidateCard> {
    try {
      const response = await atsHttp.put<AtsCandidateCard>(`/candidates/${candidateId}/card`, payload);
      return response.data;
    } catch (error) {
      const response = (error as { response?: { status?: number; data?: unknown } }).response;
      const fieldErrors = parseAtsValidationErrors(response?.data);
      const data = response?.data as { error?: { code?: string; message?: string } } | undefined;
      if (response?.status === 422 && (fieldErrors.length || data?.error?.code === 'validation_error'
        || data?.error?.message === 'Request validation failed')) throw new AtsCardValidationError(fieldErrors);
      throw new Error(getAtsErrorMessage(error, 'Не удалось сохранить карточку кандидата'));
    }
  },

  async deleteCandidateCard(candidateId: string): Promise<void> {
    await atsHttp.delete(`/candidates/${candidateId}/card`);
  },

  async moveCandidateFolder(candidateId: string, folderPath: string): Promise<AtsCandidateCard> {
    const response = await atsHttp.put<AtsCandidateCard>(`/candidates/${candidateId}/folder`, {
      folder_path: folderPath,
    });
    return response.data;
  },

  async renameFolder(folderPath: string, newName: string): Promise<{ folder_path: string }> {
    const response = await atsHttp.patch<{ folder_path: string }>(`/candidates/folders/${encodeURIComponent(folderPath)}`, {
      new_name: newName,
    });
    return response.data;
  },

  async deleteFolder(
    folderPath: string,
    options: { withResumes?: boolean } = {},
  ): Promise<{ folder_path: string; moved_documents?: number; updated_cards?: number }> {
    const response = await atsHttp.delete<{ folder_path: string; moved_documents?: number; updated_cards?: number }>(
      `/candidates/folders/${encodeURIComponent(folderPath)}`,
      options.withResumes ? { params: { with_resumes: true } } : undefined,
    );
    return response.data;
  },

  async listFolders(): Promise<AtsFolderListResponse> {
    const response = await atsHttp.get<AtsFolderListResponse>('/candidates/folders');
    return {
      paths: response.data.paths || [],
      counts: response.data.counts || {},
      total: response.data.total ?? 0,
    };
  },

  async createFolder(parentPath: string | null, newName: string): Promise<{ folder_path: string }> {
    const response = await atsHttp.post<{ folder_path: string }>('/candidates/folders', {
      parent_path: parentPath,
      new_name: newName,
    });
    return response.data;
  },

  async moveFolder(folderPath: string, targetParentPath: string): Promise<{ folder_path: string }> {
    const response = await atsHttp.put<{ folder_path: string }>(
      `/candidates/folders/${encodeURIComponent(folderPath)}/move`,
      { target_parent_path: targetParentPath },
    );
    return response.data;
  },

  async addComment(candidateId: string, text: string, author?: string): Promise<AtsCandidateCard> {
    const response = await atsHttp.post<AtsCandidateCard>(`/candidates/${candidateId}/comments`, { text, author });
    return response.data;
  },

  async updateComment(candidateId: string, commentId: string, text: string): Promise<AtsCandidateCard> {
    const response = await atsHttp.put<AtsCandidateCard>(`/candidates/${candidateId}/comments/${commentId}`, { text });
    return response.data;
  },

  async deleteComment(candidateId: string, commentId: string): Promise<AtsCandidateCard> {
    const response = await atsHttp.delete<AtsCandidateCard>(`/candidates/${candidateId}/comments/${commentId}`);
    return response.data;
  },

  async addLabel(candidateId: string, item: AtsJson | string): Promise<AtsCandidateCard> {
    const response = await atsHttp.post<AtsCandidateCard>(`/candidates/${candidateId}/labels`, { item });
    return response.data;
  },

  async deleteLabel(candidateId: string, labelId: string): Promise<AtsCandidateCard> {
    const response = await atsHttp.delete<AtsCandidateCard>(`/candidates/${candidateId}/labels/${labelId}`);
    return response.data;
  },

  async updateLabel(candidateId: string, labelId: string, item: AtsJson): Promise<AtsCandidateCard> {
    const response = await atsHttp.put<AtsCandidateCard>(`/candidates/${candidateId}/labels/${labelId}`, { item });
    return response.data;
  },

  async addRedFlag(candidateId: string, item: AtsJson): Promise<AtsCandidateCard> {
    const response = await atsHttp.post<AtsCandidateCard>(`/candidates/${candidateId}/red-flags`, { item });
    return response.data;
  },

  async deleteRedFlag(candidateId: string, redFlagId: string): Promise<AtsCandidateCard> {
    const response = await atsHttp.delete<AtsCandidateCard>(`/candidates/${candidateId}/red-flags/${redFlagId}`);
    return response.data;
  },

  async updateRedFlag(candidateId: string, redFlagId: string, item: AtsJson): Promise<AtsCandidateCard> {
    const response = await atsHttp.put<AtsCandidateCard>(`/candidates/${candidateId}/red-flags/${redFlagId}`, { item });
    return response.data;
  },

  async addHistory(candidateId: string, type: string, payload: AtsJson): Promise<AtsCandidateCard> {
    const response = await atsHttp.post<AtsCandidateCard>(`/candidates/${candidateId}/history`, { type, payload });
    return response.data;
  },

  async addStage(candidateId: string, payload: AtsJson): Promise<AtsCandidateCard> {
    const response = await atsHttp.post<AtsCandidateCard>(`/candidates/${candidateId}/stages`, { payload });
    return response.data;
  },

  async addCandidateRefusal(candidateId: string, payload: AtsJson): Promise<AtsCandidateCard> {
    const response = await atsHttp.post<AtsCandidateCard>(`/candidates/${candidateId}/candidate-refusals`, { payload });
    return response.data;
  },

  async changeCandidateStatus(candidateId: string, payload: AtsJson): Promise<AtsCandidateCard> {
    const response = await atsHttp.post<AtsCandidateCard>(`/candidates/${candidateId}/status`, { payload });
    return response.data;
  },

  async uploadAttachment(
    candidateId: string,
    file: File,
    metadata: { tags?: string; folder_path?: string } = {},
  ): Promise<AtsDocument> {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const supported = new Set(['csv', 'doc', 'docx', 'heic', 'jpeg', 'jpg', 'pdf', 'png', 'rtf', 'txt', 'xls', 'xlsx']);
    if (!supported.has(extension)) {
      throw new Error(`Неподдерживаемый формат вложения: ${extension || 'без расширения'}`);
    }
    const body = new FormData();
    body.append('file', file);
    if (toTags(metadata.tags)) body.append('tags', toTags(metadata.tags) as string);
    if (metadata.folder_path) body.append('folder_path', metadata.folder_path);
    const response = await atsHttp.post<AtsDocument>(`/candidates/${candidateId}/attachments`, body);
    return response.data;
  },

  async deleteAttachment(candidateId: string, documentId: string): Promise<AtsCandidateCard> {
    const response = await atsHttp.delete<AtsCandidateCard>(`/candidates/${candidateId}/attachments/${documentId}`);
    return response.data;
  },

  async downloadDocument(documentId: string, fileName = 'document'): Promise<void> {
    const response = await atsHttp.get<Blob>(`/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  async parseResume(
    file: File,
    metadata: { source?: string; uploaded_by?: string; tags?: string; folder_path?: string } = {},
    candidateId?: string,
  ): Promise<AtsDocument> {
    const body = new FormData();
    body.append('file', file);
    Object.entries(metadata).forEach(([key, value]) => {
      if (value) body.append(key, value);
    });
    const response = await atsHttp.post<AtsDocument>('/candidates/parse', body, {
      params: candidateId ? { candidate_id: candidateId } : undefined,
      timeout: ATS_EXTRACT_TIMEOUT_MS,
    }).catch((error) => {
      if (isAtsGatewayTimeout(error)) {
        throw new AtsParseTimeoutError(ATS_PARSE_TIMEOUT_MESSAGE);
      }
      throw new Error(getAtsErrorMessage(
        error,
        'Не удалось разобрать резюме',
      ));
    });
    return response.data;
  },

  async getResumeUpdateStatus(candidateId: string, sha256: string): Promise<AtsResumeUpdateStatus> {
    const response = await atsHttp.get<AtsResumeUpdateStatus>(
      `/candidates/${encodeURIComponent(candidateId)}/resume-update-status`,
      { params: { sha256 } },
    );
    return response.data;
  },

  async checkResumeDuplicate(
    file: File,
    metadata: { folder_path?: string } = {},
  ): Promise<AtsDuplicateCheck> {
    const body = new FormData();
    body.append('file', file);
    if (metadata.folder_path) body.append('folder_path', metadata.folder_path);
    const response = await atsHttp.post<AtsDuplicateCheck>('/candidates/duplicate-check', body, {
      timeout: ATS_EXTRACT_TIMEOUT_MS,
    }).catch((error) => {
      throw new Error(getAtsErrorMessage(error, 'Не удалось проверить дубликат резюме'));
    });
    return response.data;
  },

  async importResumes(
    files: File[],
    metadata: { source?: string; uploaded_by?: string; tags?: string; folder_path?: string } = {},
    onUploadProgress?: (percent: number) => void,
  ): Promise<AtsImportJob> {
    const body = new FormData();
    files.forEach((file) => body.append('files', file));
    Object.entries(metadata).forEach(([key, value]) => {
      if (value) body.append(key, value);
    });
    const response = await atsHttp.post<AtsImportJob>('/candidates/import', body, {
      timeout: 0,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      onUploadProgress: (event) => {
        if (!onUploadProgress || !event.total) return;
        onUploadProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
      },
    });
    return response.data;
  },

  async getImportJob(jobId: string): Promise<AtsImportJob> {
    const response = await atsHttp.get<AtsImportJob>(`/candidates/import/${jobId}`);
    return response.data;
  },

  async listImportJobs(params: { page?: number; page_size?: number; status?: string } = {}): Promise<AtsImportJobListResponse> {
    const response = await atsHttp.get<AtsImportJobListResponse>('/candidates/import', { params });
    return response.data;
  },

  async downloadImportReport(jobId: string, format: 'csv' | 'xlsx'): Promise<void> {
    const response = await atsHttp.get<Blob>(`/candidates/import/${jobId}/report.${format}`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `import-report-${jobId}.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  async cancelImportJob(jobId: string): Promise<AtsImportJob> {
    const response = await atsHttp.post<AtsImportJob>(`/candidates/import/${jobId}/cancel`);
    return response.data;
  },

  async restartImportJob(jobId: string): Promise<AtsImportJob> {
    const response = await atsHttp.post<AtsImportJob>(`/candidates/import/${jobId}/restart`);
    return response.data;
  },

  async resumePendingImportJob(jobId: string): Promise<AtsImportJob> {
    const response = await atsHttp.post<AtsImportJob>(`/candidates/import/${jobId}/resume-pending`);
    return response.data;
  },

  async retryFailedImportJob(jobId: string, options?: { onlyPageLimit?: boolean }): Promise<AtsImportJob> {
    const endpoint = `/candidates/import/${jobId}/retry-failed`;
    const response = options?.onlyPageLimit
      ? await atsHttp.post<AtsImportJob>(endpoint, undefined, { params: { only_page_limit: true } })
      : await atsHttp.post<AtsImportJob>(endpoint);
    return response.data;
  },

  async listDuplicateGroups(): Promise<AtsDuplicateGroup[]> {
    try {
      const response = await atsHttp.get<AtsDuplicateGroup[]>('/candidates/duplicates');
      return response.data;
    } catch (error) {
      throw new Error(getAtsErrorMessage(error, 'Не удалось загрузить дубли. Повторите попытку.'));
    }
  },

  async getDuplicateOverview(): Promise<AtsDuplicateOverview> {
    const response = await atsHttp.get<AtsDuplicateOverview>('/candidates/duplicates/overview');
    return response.data;
  },

  async refreshDuplicateOverview(): Promise<AtsDuplicateOverview> {
    const response = await atsHttp.post<AtsDuplicateOverview>('/candidates/duplicates/refresh');
    return response.data;
  },

  async mergeDuplicates(payload: {
    primary_candidate_id: string;
    merged_candidate_ids: string[];
    group_id?: string;
  }): Promise<AtsDuplicateGroup> {
    const response = await atsHttp.post<AtsDuplicateGroup>('/candidates/merge', payload);
    return response.data;
  },
};

export { atsHttp };
